import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import useGraphStore from '@/store/graphStore';
import { useLanguage } from '@/context/LanguageContext';

const NodeDetails = ({ className }) => {
  const { language } = useLanguage();
  const { selectedNode, hoveredNode, setSelectedNode, filteredEdges } = useGraphStore();
  
  const node = selectedNode || hoveredNode;
  
  const t = {
    nodeDetails: language === 'de' ? 'Knoten-Details' : 'Node Details',
    address: language === 'de' ? 'Adresse' : 'Address',
    label: language === 'de' ? 'Bezeichnung' : 'Label',
    balance: language === 'de' ? 'Guthaben' : 'Balance',
    transactions: language === 'de' ? 'Transaktionen' : 'Transactions',
    connections: language === 'de' ? 'Verbindungen' : 'Connections',
    lastActivity: language === 'de' ? 'Letzte Aktivität' : 'Last Activity',
    chain: language === 'de' ? 'Blockchain' : 'Chain',
    risk: language === 'de' ? 'Risiko' : 'Risk',
    inflow: language === 'de' ? 'Eingang' : 'Inflow',
    outflow: language === 'de' ? 'Ausgang' : 'Outflow',
    copy: language === 'de' ? 'Kopieren' : 'Copy',
    explore: language === 'de' ? 'Erkunden' : 'Explore',
    noSelection: language === 'de' ? 'Knoten auswählen für Details' : 'Select a node for details',
    low: language === 'de' ? 'Niedrig' : 'Low',
    medium: language === 'de' ? 'Mittel' : 'Medium',
    high: language === 'de' ? 'Hoch' : 'High',
  };
  
  if (!node) {
    return (
      <Card className={cn("p-4", className)}>
        <p className="text-sm text-muted-foreground text-center py-8">
          <i className="fa-solid fa-hand-pointer mb-2 block text-lg"></i>
          {t.noSelection}
        </p>
      </Card>
    );
  }
  
  // Calculate inflow/outflow for this node
  const nodeEdges = filteredEdges.filter(
    e => e.source === node.id || e.target === node.id ||
         e.source?.id === node.id || e.target?.id === node.id
  );
  
  const inflowAmount = nodeEdges
    .filter(e => (e.target === node.id || e.target?.id === node.id))
    .reduce((sum, e) => sum + e.amount, 0);
  
  const outflowAmount = nodeEdges
    .filter(e => (e.source === node.id || e.source?.id === node.id))
    .reduce((sum, e) => sum + e.amount, 0);
  
  const riskLabels = { low: t.low, medium: t.medium, high: t.high };
  const riskColors = {
    low: 'bg-success/10 text-success border-success/20',
    medium: 'bg-warning/10 text-warning border-warning/20',
    high: 'bg-destructive/10 text-destructive border-destructive/20'
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(node.address);
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className={cn("overflow-hidden", className)}>
      {/* Header */}
      <div className="p-3 border-b border-border bg-muted/30 flex items-center justify-between">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <div className={cn(
            "w-3 h-3 rounded-full",
            node.risk === 'high' ? 'bg-destructive' : node.risk === 'medium' ? 'bg-warning' : 'bg-success'
          )}></div>
          {t.nodeDetails}
        </h4>
        {selectedNode && (
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedNode(null)}>
            <i className="fa-solid fa-xmark text-xs"></i>
          </Button>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Label & Chain */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-medium">{node.label}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">
                <i className={cn(
                  "mr-1",
                  node.chain === 'BTC' ? 'fa-brands fa-bitcoin text-crypto-bitcoin' : 'fa-brands fa-ethereum text-crypto-ethereum'
                )}></i>
                {node.chain}
              </Badge>
              <Badge variant="outline" className={cn("text-xs border", riskColors[node.risk])}>
                {riskLabels[node.risk]}
              </Badge>
            </div>
          </div>
        </div>
        
        {/* Address */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">{t.address}</p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-muted px-2 py-1 rounded flex-1 overflow-hidden text-ellipsis">
              {node.address.slice(0, 10)}...{node.address.slice(-8)}
            </code>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={copyToClipboard}>
              <i className="fa-solid fa-copy text-xs"></i>
            </Button>
          </div>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-muted/30 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">{t.balance}</p>
            <p className="font-mono font-medium">{node.balance.toFixed(4)} {node.chain}</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">{t.connections}</p>
            <p className="font-mono font-medium">{node.degree}</p>
          </div>
          <div className="bg-success/10 rounded-lg p-3">
            <p className="text-xs text-success">{t.inflow}</p>
            <p className="font-mono font-medium text-success">+{inflowAmount.toFixed(4)}</p>
          </div>
          <div className="bg-destructive/10 rounded-lg p-3">
            <p className="text-xs text-destructive">{t.outflow}</p>
            <p className="font-mono font-medium text-destructive">-{outflowAmount.toFixed(4)}</p>
          </div>
        </div>
        
        {/* Last Activity */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">{t.lastActivity}</p>
          <p className="text-sm">{formatDate(node.lastActivity)}</p>
        </div>
        
        {/* Tx Count */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">{t.transactions}</p>
          <p className="text-sm font-mono">{node.txCount.toLocaleString()}</p>
        </div>
      </div>
    </Card>
  );
};

export default NodeDetails;
